import { cn } from "@/lib/utils";
import React, { useEffect, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import SectionSeparator from "../hoc/SectionSeparator";
import { Link } from "react-router-dom";
import { LuLogOut, LuUser } from "react-icons/lu";
import { RiChat3Fill, RiGeminiFill } from "@remixicon/react";
import { useGlobalStore } from "@/GlobalStore";
import { useKeycloak } from "@react-keycloak/web";
import AccountsDropdown from "../hoc/accounts/AccountsDropdown";
import { NAVBAR_ITEM } from "../hoc/Navbar";
import { logout } from "@/utils/logout";
import { hasUnseenEntries } from "../common/whats-new/WhatsNew";

// Type declaration for Pylon
declare global {
  interface Window {
    Pylon?: (action: string, callback?: () => void) => void;
  }
}

export interface SidenavProps extends React.HTMLAttributes<HTMLDivElement> {
  tabs: NAVBAR_ITEM[];
  selectedTab: NAVBAR_ITEM | null;
  onTabClick: (tab: NAVBAR_ITEM) => void;
  isOpen: boolean;
  children?: React.ReactNode;
  onAnyDialogOpenChange?: (open: boolean) => void;
  whatsNewOpen: boolean;
  onWhatsNewToggle: () => void;
}

export const Sidenav = React.forwardRef<HTMLDivElement, SidenavProps>(
  (
    {
      className,
      tabs,
      selectedTab,
      onTabClick,
      isOpen,
      onAnyDialogOpenChange,
      whatsNewOpen,
      onWhatsNewToggle,
      ...props
    },
    ref,
  ) => {
    const { keycloak } = useKeycloak();
    const profilePicture = useGlobalStore((state) => state.profilePicture);
    const userName = useGlobalStore((state) => state.userName);
    const userEmail = useGlobalStore((state) => state.userEmail);

    const [hasUnseen, setHasUnseen] = useState(() => hasUnseenEntries());

    useEffect(() => {
      setHasUnseen(hasUnseenEntries());
    }, [whatsNewOpen]);

    const isPylonOpenRef = useRef(false);

    useEffect(() => {
      window.Pylon?.("onShow", () => {
        isPylonOpenRef.current = true;
      });
      window.Pylon?.("onHide", () => {
        isPylonOpenRef.current = false;
      });

      const handleClickOutside = (e: MouseEvent) => {
        if (!isPylonOpenRef.current) return;
        const target = e.target as HTMLElement;
        const chatWindow = document.querySelector(".PylonChat-chatWindowFrameContainer");
        const launcher = document.getElementById("custom-intercom-launcher");
        if (chatWindow?.contains(target) || launcher?.contains(target)) return;
        window.Pylon?.("hide");
      };

      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    const togglePylonChat = () => {
      if (isPylonOpenRef.current) {
        window.Pylon?.("hide");
      } else {
        window.Pylon?.("show");
      }
    };

    return (
      <aside
        ref={ref}
        className={cn(
          `flex flex-col justify-between flex-grow 
          h-full p-[6px] rounded-[8px] backdrop-blur-[6px]
          transition-all duration-300 ease-in-out 
          squircle squircle-fill-[#021859E5] squircle-radius-[8px] squircle-smooth-[1]`,
          isOpen ? "w-[130px]" : "w-[38px]",
          className,
        )}
        {...props}
      >
        <div className="flex flex-col  gap-4">
          {/* Accounts Dropdowns */}
          <AccountsDropdown
            expanded={isOpen}
            onAnyDialogOpenChange={onAnyDialogOpenChange}
          />
          {/* Menu Items */}
          <div
            className={cn(
              "w-full flex flex-col items-center gap-3 transition-all duration-300",
              isOpen ? "justify-start" : "justify-center",
            )}
          >
            {tabs.map((item) => (
              <button
                key={item.key}
                onClick={() => onTabClick(item)}
                className={cn(
                  "w-full flex items-center cursor-pointer transition-colors duration-300 h-[26px] p-[6px]",
                  isOpen ? "gap-2" : "justify-start",
                  selectedTab?.key === item.key
                    ? "squircle squircle-fill-[#364D8C] squircle-radius-[6px] squircle-smooth-[1]"
                    : "hover:squircle hover:squircle-fill-[#364D8C66] hover:squircle-radius-[6px] hover:squircle-smooth-[1]",
                )}
              >
                <span
                  className={cn(
                    "min-w-[14px]",
                    selectedTab?.key === item.key ? "text-white" : "text-[#7883a7]",
                  )}
                >
                  {item.icon}
                </span>
                <div
                  className={cn(
                    "flex-1 flex flex-col items-start justify-start overflow-hidden transition-opacity duration-300",
                    isOpen ? "opacity-100" : "opacity-0 ml-0 pointer-events-none",
                  )}
                >
                  {isOpen && (
                    <p
                      className={cn(
                        "w-full text-left text-[12px] font-normal not-italic leading-normal overflow-hidden text-ellipsis whitespace-nowrap relative top-[1.3px]",
                        selectedTab?.key === item.key ? "text-white" : "text-white/60",
                      )}
                    >
                      {item.label}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Navbar Footer */}
        <div className="flex flex-col gap-[6px] items-start">
          {/* What's New */}
          <div
            className={cn(
              "w-full flex items-center gap-3 transition-all duration-300",
              isOpen ? "justify-start" : "justify-center",
            )}
          >
            <div
              className="relative flex w-full items-center cursor-pointer hover:squircle hover:squircle-fill-[#273d78] hover:squircle-radius-[6px] hover:squircle-smooth-[1] gap-2"
              onClick={onWhatsNewToggle}
            >
              <div
                className={cn(
                  "relative flex items-center justify-center squircle squircle-radius-[6px] squircle-smooth-[1] h-[26px] w-[26px] min-w-[26px]",
                  whatsNewOpen || hasUnseen ? "squircle-fill-[#EBEDF2]" : "squircle-fill-[#35467A]",
                )}
              >
                <svg width="0" height="0" style={{ position: "absolute" }}>
                  <defs>
                    <linearGradient
                      id="whats-new-gradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor="#FF4500" />
                      <stop offset="100%" stopColor="#FF4699" />
                    </linearGradient>
                  </defs>
                </svg>
                <RiGeminiFill
                  size={17}
                  style={{
                    fill: whatsNewOpen || hasUnseen ? "url(#whats-new-gradient)" : "white",
                  }}
                />
                {hasUnseen && !whatsNewOpen && (
                  <span className="absolute -top-[2px] -right-[2px] h-[8px] w-[8px] rounded-full bg-[#F04438] outline outline-[2px] outline-[#1B2F69]" />
                )}
              </div>
              <div
                className={cn(
                  "flex-1 flex flex-col items-start justify-start overflow-hidden transition-opacity duration-300",
                  isOpen ? "opacity-100" : "opacity-0 pointer-events-none",
                )}
              >
                {isOpen && (
                  <p className="w-full text-left text-white/60 text-[12px] font-normal not-italic leading-normal overflow-hidden text-ellipsis whitespace-nowrap relative top-[1.3px]">
                    What's new
                  </p>
                )}
              </div>
            </div>
          </div>
          {/* Support */}
          <div
            className={cn(
              "w-full flex items-center gap-3 transition-all duration-300",
              isOpen ? "justify-start" : "justify-center",
            )}
          >
            <div
              className="flex w-full items-center cursor-pointer hover:squircle hover:squircle-fill-[#273d78] hover:squircle-radius-[6px] hover:squircle-smooth-[1] gap-2"
              id="custom-intercom-launcher"
              onClick={togglePylonChat}
            >
              <div className="flex items-center justify-center squircle squircle-fill-[#008fdc] squircle-radius-[6px] squircle-smooth-[1] h-[26px] w-[26px] min-w-[26px]">
                <RiChat3Fill className="text-white" size={17} />
              </div>
              <div
                className={cn(
                  "flex-1 flex flex-col items-start justify-start overflow-hidden transition-opacity duration-300",
                  isOpen ? "opacity-100" : "opacity-0 pointer-events-none",
                )}
              >
                {isOpen && (
                  <p className="w-full text-left text-white/60 text-[12px] font-normal not-italic leading-normal overflow-hidden text-ellipsis whitespace-nowrap relative top-[1.3px]">
                    Support
                  </p>
                )}
              </div>
            </div>
          </div>
          <div
            className={cn(
              "w-full flex items-center gap-3 transition-all duration-300 hover:squircle  hover:squircle-fill-[#273d78] hover:squircle-radius-[6px] hover:squircle-smooth-[1]",
              isOpen ? "justify-start" : "justify-center",
            )}
          >
            <Popover>
              <PopoverTrigger className="w-full flex items-center">
                {profilePicture ? (
                  <img
                    src={profilePicture}
                    alt="Profile"
                    className="h-[26px] w-[26px] squircle-mask squircle-radius-[6px] squircle-smooth-[1]"
                  />
                ) : (
                  <div className="flex items-center justify-center squircle squircle-fill-[#008fdc] squircle-radius-[6px] squircle-smooth-[1] h-[26px] w-[26px]">
                    <span className="flex items-center justify-center relative top-[1.3px] text-[14px] text-white h-[26px] w-[26px]">
                      {(keycloak.idTokenParsed?.preferred_username as string)
                        ?.charAt(0)
                        .toUpperCase()}
                    </span>
                  </div>
                )}
                <div
                  className={cn(
                    "flex-1 flex flex-col items-start justify-start overflow-hidden transition-opacity duration-300",
                    isOpen ? "opacity-100 ml-2" : "opacity-0 ml-0 pointer-events-none",
                  )}
                >
                  {isOpen && (
                    <p className="w-full text-left text-[#FFF] text-[12px] font-normal not-italic leading-normal overflow-hidden text-ellipsis whitespace-nowrap relative top-[1.3px]">
                      {userName ? userName?.split(" ")[0] : userEmail}
                    </p>
                  )}
                </div>
              </PopoverTrigger>
              <PopoverContent
                side="right"
                sideOffset={2}
                align="start"
                className="text-[14px] font-[400] text-[#09090B] w-fit min-w-[165px] p-0 mb-[14px] rounded-[2px]"
              >
                <div className="font-[600] px-[12px] py-[6px]">Starter Plan</div>
                <SectionSeparator className="h-[1px] bg-[#E4E4E7]" />
                <Link
                  to="/profile"
                  className="block px-[12px] py-[6px] cursor-pointer hover:bg-[#EFF2FB]"
                >
                  <div className="flex items-center gap-[8px]">
                    <LuUser />
                    <span>Profile</span>
                  </div>
                </Link>
                <div
                  className="text-[#D92D20] px-[12px] py-[6px] hover:bg-[#D92D200D] cursor-pointer flex items-center gap-[8px]"
                  onClick={() => {
                    logout("manual");
                  }}
                >
                  <LuLogOut />
                  <span>Logout</span>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </aside>
    );
  },
);

Sidenav.displayName = "Sidenav";
